import { z } from 'zod';

/**
 * One row of `GET /api/licenses/audit` — the frozen HTTP contract
 * (`testdata/golden/http-contracts/admin-licenses.json`, #147), reproduced 1:1
 * by the Rust handler (`rust/server/src/routes/licenses/read.rs`):
 *
 * - `timestamp` (ISO-8601), NOT `createdAt`;
 * - `event` is the raw `audit_logs.operation` with only the `license.` prefix
 *   stripped (`operationToEvent`) — operator and machine rows therefore carry
 *   names like `auth.login_success`, `auth.logout`, `offline_activated`,
 *   `rate_limit.exceeded`, `bridge_session.issued`, `factory_usb.issued`, which
 *   are NOT a closed enum (a strict enum here is what broke the journal);
 * - `ipAddress` is `null` when the row has no client IP (`repo::audit`).
 */
export const auditEventSchema = z.object({
  id: z.string(),
  licenseId: z.string().nullable(),
  serial: z.string(),
  event: z.string(),
  performedBy: z.string(),
  ipAddress: z.string().nullable(),
  timestamp: z.string(),
  metadata: z.record(z.unknown()).optional(),
});

export const auditListResponseSchema = z.object({
  data: z.array(auditEventSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  pages: z.number().int(),
});

/** A wire row as parsed above. */
export type AuditWireEvent = z.infer<typeof auditEventSchema>;

/** The row shape the audit UI renders — the wire row with `timestamp` surfaced as `createdAt`. */
export interface AuditEvent {
  id: string;
  licenseId?: string;
  serial: string;
  event: string;
  performedBy: string;
  ipAddress?: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export interface AuditListResponse {
  data: AuditEvent[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/** Wire row → UI row. */
export function toAuditEvent(row: AuditWireEvent): AuditEvent {
  return {
    id: row.id,
    serial: row.serial,
    event: row.event,
    performedBy: row.performedBy,
    createdAt: row.timestamp,
    // `exactOptionalPropertyTypes`: omit the optional keys rather than set `undefined`.
    ...(row.licenseId !== null ? { licenseId: row.licenseId } : {}),
    ...(row.ipAddress !== null ? { ipAddress: row.ipAddress } : {}),
    ...(row.metadata !== undefined ? { metadata: row.metadata } : {}),
  };
}
